import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { RoutineCustomization } from "@src/features/alarms/components/RoutineCustomization";
import { useMemo, useLayoutEffect, useCallback, useState } from "react";
import { Pressable, ActivityIndicator } from "react-native";
import { Text, View } from "@src/components";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { isWeb } from "@src/utils/web-helpers";
import { useUnistyles } from "react-native-unistyles";
import { useAlarmsByCameras, useUpdateAlarmRoutines } from "@src/services/api/hooks";
import type { RotatingAlarmRoutine, WeeklyAlarmRoutine } from "@visioni/a3s-alarm-schedules/schema";

export default function EditRoutineScreen() {
  const { theme } = useUnistyles();
  const router = useRouter();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();
  const updateAlarmRoutines = useUpdateAlarmRoutines();
  const selectedSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const { cameraKeys: cameraKeysParam, routine: routineParam } = useLocalSearchParams<{
    cameraKeys: string;
    routine?: string;
  }>();

  const cameraKeys = useMemo(() => {
    if (!cameraKeysParam) return [];
    try {
      return JSON.parse(cameraKeysParam) as string[];
    } catch {
      return [];
    }
  }, [cameraKeysParam]);

  const effectiveSiteId = selectedSiteId ?? "";
  // Fetch alarm data for each camera to get timezones
  const alarmQueries = useAlarmsByCameras(effectiveSiteId, cameraKeys);

  const [routineData, setRoutineData] = useState<WeeklyAlarmRoutine | RotatingAlarmRoutine | null>(null);

  const initialRoutine = useMemo(() => {
    if (!routineParam) return undefined;
    try {
      const decoded = decodeURIComponent(routineParam);
      return JSON.parse(decoded) as WeeklyAlarmRoutine | RotatingAlarmRoutine;
    } catch (error) {
      console.error("Failed to parse routine from params:", error);
      return undefined;
    }
  }, [routineParam]);

  const isSaving = updateAlarmRoutines.isPending;

  const handleSave = useCallback(async () => {
    if (!selectedSiteId || !routineData || cameraKeys.length === 0) {
      showErrorToast({ message: "Error", description: "Please configure a routine before saving." });
      return;
    }

    const cameras = cameraKeys.map((cameraKey, index) => {
      const alarmData = alarmQueries[index]?.data;
      const timezone = getTimezoneFromAlarmDetail(alarmData) || alarmData?.settings?.timezone || "UTC";
      return { cameraKey, timezone };
    });

    updateAlarmRoutines.mutate(
      { siteId: selectedSiteId, cameras, routine: routineData },
      {
        onSuccess: () => {
          showSuccessToast({ message: `Routine saved` });
          router.dismissTo("/alarm-settings/manage-routines");
        },
        onError: (error) => {
          showErrorToast({ message: "Error", description: "Failed to save routine. Please try again." });
          console.error("Failed to save routine:", error);
        },
      },
    );
  }, [selectedSiteId, cameraKeys, routineData, alarmQueries, updateAlarmRoutines, router]);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Edit Routine",
      headerBackButtonDisplayMode: "minimal",
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerLeft,
      headerRight: () => (
        <Pressable onPress={handleSave} style={{ paddingHorizontal: 16 }} disabled={isSaving || !routineData}>
          {isSaving ?
            <ActivityIndicator size="small" />
          : <Text textStyle="md/semibold" color={routineData ? "navy" : "textMuted"}>
              Save
            </Text>
          }
        </Pressable>
      ),
    });
  }, [navigation, headerLeft, handleSave, routineData, isSaving, theme.colors.background, theme.colors.text]);

  return (
    <View flex={1}>
      {isWeb && (
        <View px="5xl" pt="4xl" pb="xl" bg="background" alignItems="flex-end">
          <Pressable onPress={handleSave} disabled={isSaving || !routineData}>
            {isSaving ?
              <ActivityIndicator size="small" />
            : <Text textStyle="md/semibold" color={routineData ? "navy" : "textMuted"}>
                Save
              </Text>
            }
          </Pressable>
        </View>
      )}
      <RoutineCustomization onSave={setRoutineData} initialRoutine={initialRoutine} showHeader={false} />
    </View>
  );
}
